/**
 * Whether what is on screen came from the daemon or from the copy on disk.
 *
 * The client can now come up with no network at all: the identity probe and the
 * session document are replayed from the last good answer, and the mail itself
 * is served out of the local replica. That is the point of keeping a replica —
 * and it is also how a mail client can quietly lie to somebody, because a list
 * of saved mail looks exactly like a list of current mail. A reader deciding
 * whether they have heard from someone needs to know which one they are looking
 * at.
 *
 * So this is deliberately not a "connection status". It is not derived from
 * `navigator.onLine`, which is only trustworthy in one direction, and not from
 * the event stream, which can be down while requests still succeed. It is set
 * from what the last answer actually was — `served_locally` on the reply — so
 * it describes the mail on screen rather than a guess about the network.
 *
 * # Why it is here rather than on the transport contract
 *
 * Because it is a fact about this session, not a capability, and the two
 * builds do not disagree about it: a browser has no replica, so nothing ever
 * sets it and it stays false, which is true rather than a stub. Putting it on
 * `Transport` would have meant a null-shaped capability and a second thing for
 * `net.web.ts` to implement in order to say "no".
 */

import { signal } from "@preact/signals";

/**
 * True while the last answer came from the local replica.
 *
 * Set by the desktop transport on every reply, so it clears itself the moment
 * the daemon answers again — no timer, and nothing to reset on reconnect.
 */
export const showingSaved = signal(false);
